import React from 'react';
import { User, LogOut, Award, Sparkles } from 'lucide-react';
import { truncateAddress } from '../utils/chartHelpers';
import { RANK_COLORS, RANK_THRESHOLDS, UserRank, calculatePointMultiplier } from '../types/user';

interface ProfileButtonProps {
  username: string;
  address: string;
  stakedAmount?: number;
  totalPoints?: number;
  rank?: UserRank;
  onDisconnect?: () => void;
}

function getRankColor(rank: UserRank): string {
  return RANK_COLORS[rank].text;
}

function getRankGradient(rank: UserRank): string {
  return RANK_COLORS[rank].gradient;
}

// Calculate progress to next rank based on current rank thresholds
function calculateProgressToNextRank(points: number, currentRank: UserRank): number {
  switch (currentRank) {
    case 'Ronin':
      return (points / RANK_THRESHOLDS.SAMURAI.min) * 100;
    case 'Samurai':
      return ((points - RANK_THRESHOLDS.SAMURAI.min) / (RANK_THRESHOLDS.GENERAL.min - RANK_THRESHOLDS.SAMURAI.min)) * 100;
    case 'General':
      return ((points - RANK_THRESHOLDS.GENERAL.min) / (RANK_THRESHOLDS.SHOGUN.min - RANK_THRESHOLDS.GENERAL.min)) * 100;
    case 'Shogun':
      return 100; // Already at max rank
    default:
      return 0;
  }
}

// Get next rank threshold based on current rank
function getNextRankThreshold(currentRank: UserRank): number {
  switch (currentRank) {
    case 'Ronin':
      return RANK_THRESHOLDS.SAMURAI.min;
    case 'Samurai':
      return RANK_THRESHOLDS.GENERAL.min;
    case 'General':
      return RANK_THRESHOLDS.SHOGUN.min;
    case 'Shogun':
      return RANK_THRESHOLDS.SHOGUN.min;
    default:
      return 0;
  }
}

export function ProfileButton({ 
  username, 
  address, 
  stakedAmount = 0, 
  totalPoints = 0,
  rank = 'Ronin',
  onDisconnect 
}: ProfileButtonProps) {
  const rankColor = getRankColor(rank);
  const rankGradient = getRankGradient(rank);
  const pointMultiplier = calculatePointMultiplier(stakedAmount);
  const hasMultiplier = pointMultiplier > 1;
  const progressPercentage = calculateProgressToNextRank(totalPoints, rank);
  const nextRankThreshold = getNextRankThreshold(rank);

  return (
    <div className="relative group">
      <button
        className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 transition-colors flex items-center justify-center overflow-hidden"
      >
        <User className="w-5 h-5 text-white" />
      </button>

      {/* Hover card */}
      <div className="absolute right-0 top-full mt-2 w-72 bg-black/95 backdrop-blur-sm rounded-lg border border-white/10 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 p-8 z-50">
        <div className="flex flex-col items-center space-y-6">
          {/* Profile Image */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br bg-opacity-80 flex items-center justify-center p-1">
              <img
                src="https://i.ibb.co/p6tjyT7C/676ef7b23bdebce80077f770.jpg"
                alt="Profile"
                className="w-full h-full rounded-full object-cover"
              />
            </div>
          </div>

          {/* Username and Rank */}
          <div className="text-center">
            <h3 className="text-2xl font-bold">{username}</h3>
            <div className="flex items-center justify-center gap-1 mt-1">
              <Award className={`w-4 h-4 ${rankColor}`} />
              <p className={`text-sm font-medium ${rankColor}`}>{rank}</p>
            </div>
            <p className="text-sm text-gray-400 mt-1">{truncateAddress(address)}</p>
          </div>

          {/* Points and Progress */}
          <div className="w-full space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">Total Points</span>
              <div className="flex items-center gap-2">
                <span className={`font-medium ${rankColor}`}>{totalPoints}</span>
                {hasMultiplier && (
                  <div className="flex items-center gap-1 bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded-full text-xs font-medium">
                    <Sparkles className="w-3 h-3" />
                    {pointMultiplier}x
                  </div>
                )}
              </div>
            </div>
            <div className="w-full bg-black/60 rounded-full h-2">
              <div 
                className={`bg-gradient-to-r ${rankGradient} h-2 rounded-full transition-all duration-300`}
                style={{ width: `${Math.min(100, progressPercentage)}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 text-center">
              {rank !== 'Shogun' ? (
                <>
                  {totalPoints.toLocaleString()} / {nextRankThreshold.toLocaleString()} points to next rank
                  ({Math.round(progressPercentage)}%)
                </>
              ) : (
                'Maximum rank achieved'
              )}
            </p>
          </div>

          {/* Staked Amount */}
          <div className="w-full bg-black/80 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-300">Staked</span>
              <span className="text-xl font-bold">{stakedAmount.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-gray-400">$SHOGUN</span>
              {hasMultiplier && (
                <span className="text-xs text-yellow-500">
                  {pointMultiplier}x Point Multiplier
                </span>
              )}
            </div>
          </div>

          {/* Disconnect Button */}
          <button
            onClick={onDisconnect}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600/90 hover:bg-red-600 rounded-lg text-white text-sm font-medium transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Disconnect</span>
          </button>
        </div>
      </div>
    </div>
  );
}